import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { DropdownMenu } from '@gravity-ui/uikit'
import { Heart, Envelope, Person, Bars, Xmark } from '@gravity-ui/icons'
import liluCrestMark from '@/assets/home/lilu-crest-mark.svg'
import type { SharedProps } from '@/types'
import { useT } from '@/i18n'
import { becomeSitterHref } from '@/lib/sitterSignup'
import LanguageSwitcher from '@/components/LanguageSwitcher'

const iconBox: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  background: '#f3f2ef',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--lilu-ink)',
}

export default function Header() {
  const { current_user, unread_messages_count } = usePage<{
    current_user?: SharedProps['current_user']
    unread_messages_count?: number
  }>().props
  const unread = unread_messages_count ?? 0
  const t = useT()
  const [mobileOpen, setMobileOpen] = useState(false)

  function logoutAction() {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/session'
    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'DELETE'
    form.appendChild(methodInput)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }
    document.body.appendChild(form)
    form.submit()
  }

  function buildMenuItems() {
    if (!current_user) return []

    if (!current_user.registration_complete) {
      return [{ action: logoutAction, text: t('Logout') }]
    }

    const items: Array<{ action: () => void; text: string }> = []

    if (current_user.has_owner_profile && current_user.owner_profile_id) {
      items.push({ action: () => router.visit('/dashboard'), text: t('Dashboard') })
      items.push({ action: () => router.visit(`/owners/${current_user.owner_profile_id}`), text: t("Owner's profile") })
      items.push({ action: () => router.visit('/orders'), text: t('My requests') })
    } else {
      items.push({ action: () => router.visit('/owners/new'), text: t('Become an owner') })
    }

    if (current_user.has_sitter_profile && current_user.sitter_profile_id) {
      items.push({ action: () => router.visit('/sitter/dashboard'), text: t('Sitter dashboard') })
      items.push({ action: () => router.visit(`/sitters/${current_user.sitter_profile_id}`), text: t("Sitter's profile") })
      items.push({ action: () => router.visit('/orders/available'), text: t('Available requests') })
      items.push({ action: () => router.visit('/my_responses'), text: t('My Responses') })
      items.push({ action: () => router.visit('/subscription'), text: t('Subscription') })
    } else {
      items.push({ action: () => router.visit('/sitters/new'), text: t('Become a sitter') })
    }

    if (current_user.role === 'admin') {
      items.push({ action: () => router.visit('/admin'), text: t('Admin') })
    }

    items.push({ action: () => router.visit('/settings'), text: t('Settings') })
    items.push({ action: logoutAction, text: t('Logout') })

    return items
  }

  return (
    <header
      className="lilu-header"
      style={{
        background: '#ffffff',
        borderBottom: '1px solid #efefec',
        padding: '14px 40px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      <div
        style={{
          maxWidth: 1320,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <Link href="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <img src={liluCrestMark} alt="" width={32} height={38} style={{ width: 32, height: 38, display: 'block' }} />
          <span className="lilu-header__logo-word" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 25, color: '#000', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
            Lilu Pets
          </span>
        </Link>

        <nav className="lilu-header__nav" style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <Link href="/" className="lilu-link">
            {t('Find a pet sitter')}
          </Link>
          <Link href={becomeSitterHref(current_user)} className="lilu-link">
            {t('Become a pet sitter')}
          </Link>
        </nav>

        <div className="lilu-header__actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={iconBox} title={t('Wishlist')}>
            <Heart width={19} height={19} />
          </span>
          <Link href="/conversations" style={{ ...iconBox, position: 'relative', textDecoration: 'none' }} title={t('Messages')}>
            <Envelope width={19} height={19} />
            {unread > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  minWidth: 18,
                  height: 18,
                  padding: '0 5px',
                  borderRadius: 9,
                  background: 'var(--lilu-orange)',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #fff',
                }}
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </Link>

          {current_user ? (
            <DropdownMenu
              items={buildMenuItems()}
              switcher={
                <button
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontWeight: 500,
                    color: 'var(--lilu-ink)',
                  }}
                >
                  <Person width={20} height={20} />
                  {current_user.first_name}
                </button>
              }
            />
          ) : (
            <Link
              href="/session/new"
              className="lilu-link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginLeft: 4 }}
            >
              <Person width={20} height={20} />
              {t('Sign In')}
            </Link>
          )}

          {/* Real switcher, now that Dutch ships. Hidden on phones — the mobile
              menu carries it instead. */}
          <span className="lilu-header__lang">
            <LanguageSwitcher />
          </span>

          <button
            className="lilu-header__burger"
            aria-label={t('Menu')}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <Xmark width={20} height={20} /> : <Bars width={20} height={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: '8px 4px 4px',
            borderTop: '1px solid #efefec',
            marginTop: 12,
          }}
        >
          <div style={{ padding: '6px 8px 10px' }}>
            <LanguageSwitcher compact />
          </div>
          <Link href="/" className="lilu-link" style={mobileLink} onClick={() => setMobileOpen(false)}>
            {t('Find a pet sitter')}
          </Link>
          <Link href={becomeSitterHref(current_user)} className="lilu-link" style={mobileLink} onClick={() => setMobileOpen(false)}>
            {t('Become a pet sitter')}
          </Link>
          {current_user ? (
            buildMenuItems().map((item) => (
              <button
                key={item.text}
                onClick={() => {
                  setMobileOpen(false)
                  item.action()
                }}
                style={{ ...mobileLink, background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', fontSize: 15 }}
              >
                {item.text}
              </button>
            ))
          ) : (
            <Link href="/session/new" className="lilu-link" style={mobileLink} onClick={() => setMobileOpen(false)}>
              {t('Sign In')}
            </Link>
          )}
        </nav>
      )}
    </header>
  )
}

const mobileLink: React.CSSProperties = {
  padding: '10px 8px',
  borderRadius: 8,
  color: 'var(--lilu-ink)',
}
